import FrameBack from "images/frame_back.svg?react";
import type { ReactNode } from "react";
import { Component } from "react";
import { useTranslation } from "react-i18next";
import Button from "components/ui/Button";
import { getSessionUser } from "services/users/auth";
import { logError } from "../../services/logging";
import "./style.css";
import ErrorBanner from "components/ui/ErrorBanner";

type ErrorBoundaryProps = {
  children: ReactNode;
  fallback?: ReactNode;
  pathname?: string;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state = {
    hasError: false,
  };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: Readonly<ErrorBoundaryProps>) {
    if (this.props.pathname !== prevProps.pathname) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error /*, _errorInfo: ErrorInfo */) {
    this.setState({ hasError: true });

    const user = getSessionUser();

    logError("Caught error in error boundary", {
      error,
      user_id: user?.id,
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || <DefaultFallback />;
    }

    return this.props.children;
  }
}

/**
 * For a boundary given no fallback of its own. Split out of the class so the
 * copy can come from the hook rather than a render prop.
 */
function DefaultFallback() {
  const { t } = useTranslation();

  return (
    <div className="error-boundary">
      <div className="container">
        <ErrorBanner>{t("Common.errors.somethingWrong")}</ErrorBanner>
        <Button as="link" to="/">
          {t("Common.actions.backHome")}
        </Button>
      </div>

      <FrameBack className="frame-back" />
    </div>
  );
}
